import { useEffect, useMemo, useState } from 'react';
import {
    fetchUserDetailsAPI,
    fetchUserFollowersAPI,
    fetchUserHistoryAPI,
    fetchUserStatsAPI,
    followUserAPI,
} from '../../api/users';
import {
    User,
    UnpopulatedEvent,
    UserReportTypes,
    EventTypes,
} from '../../utils/types';
import { fetchUserReportsAPI } from '../../api/reports';

interface UserHistoryResponse {
    eventHistory: UnpopulatedEvent[];
    nextEvents: UnpopulatedEvent[];
}

interface UserStats {
    sportEventType: EventTypes;
    eventCount: number;
    averageRating: number;
}

export interface UserReport {
    sportEvent: { id: string; name: string };
    type: UserReportTypes;
    createdAt: string;
}

export const useUserPage = (userId: string | undefined) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isMoreLoading, setIsMoreLoading] = useState(false);
    const [userDetails, setUserDetails] = useState<User | null>(null);
    const [userHistory, setUserHistory] = useState<UserHistoryResponse | null>(
        null
    );
    const [userStats, setUserStats] = useState<UserStats[] | null>(null);
    const [userFollowers, setUserFollowers] = useState<User[]>([]);
    const [isUserFollowersLoading, setIsUserFollowersLoading] = useState(false);
    const [error, setError] = useState(null);
    const [limit, setLimit] = useState(10);
    // TODO pagination
    const [eventCount, setEventCount] = useState(0);
    const [userReports, setUserReports] = useState<UserReport[]>([]);

    useEffect(() => {
        async function fetchData() {
            if (userId) {
                try {
                    if (limit > 10) {
                        setIsMoreLoading(true);
                    } else {
                        setIsLoading(true);
                    }
                    const details = await fetchUserDetailsAPI(userId);
                    // TODO implement limit to paginate
                    const history = await fetchUserHistoryAPI(userId, 5);
                    const reports = await fetchUserReportsAPI(userId);
                    const stats = await fetchUserStatsAPI(userId);
                    setUserDetails(details);
                    setUserHistory(history);
                    setUserReports(reports);
                    setUserStats(stats);
                } catch (error: any) {
                    console.warn(error);
                    setError(error);
                }
                setIsLoading(false);
                setIsMoreLoading(false);
            }
        }
        fetchData();
    }, [userId, limit]);

    const showLoadMoreButton = useMemo(
        () =>
            userHistory?.eventHistory?.length
                ? userHistory?.eventHistory?.length < eventCount
                : false,
        [userHistory, eventCount]
    );

    const onShowMoreClick = async () => {
        const newLimit = limit + 10;
        setLimit(newLimit);
    };

    async function followUser() {
        setIsLoading(true);
        try {
            const data = await followUserAPI(userId!);
            setUserDetails(data);
            setIsLoading(false);
        } catch (error: any) {
            console.warn(error);
            setError(error);
            setIsLoading(false);
        }
    }

    async function fetchUserFollowers() {
        setIsUserFollowersLoading(true);
        try {
            const data = await fetchUserFollowersAPI(userId!);
            setUserFollowers(data);
            setIsUserFollowersLoading(false);
        } catch (error: any) {
            console.warn(error);
            setError(error);
            setIsUserFollowersLoading(false);
        }
    }

    async function followUserFromDialog(dialogUserId: string) {
        setIsUserFollowersLoading(true);
        try {
            await followUserAPI(dialogUserId);
            const data = await fetchUserFollowersAPI(userId!);
            setUserFollowers(data);
            setIsUserFollowersLoading(false);
        } catch (error: any) {
            console.warn(error);
            setError(error);
            setIsUserFollowersLoading(false);
        }
    }

    return {
        isLoading,
        userDetails,
        userHistory,
        error,
        showLoadMoreButton,
        onShowMoreClick,
        isMoreLoading,
        followUser,
        fetchUserFollowers,
        userFollowers,
        isUserFollowersLoading,
        followUserFromDialog,
        userReports,
        userStats,
    };
};
